import { useState } from 'react';
import { FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { uploadPblFile } from '@/api/pbl.api';
import { InputWrapper } from '@/components/Container/AuthContainer/styles';
import { errorHandler } from '@/utils/toastHandler';

import type { UpdateVolunteerForm, VolunteerFormState } from '../types';
import { UploadFileField } from '../shared';
import { FormPanel, FormSectionTitle, HelperText, ValidationText } from '../styles';
import { Text } from '@/components/UI/Typography/Text';
import { theme } from '@/theme/Theme';
import { createLocalDocumentUrl, createPblUploadFormData, getUploadedPblFileId } from '../uploadedDocument';
import { SectionTitle } from '../../CaseProgressDetail/styles';

interface Props {
  form: VolunteerFormState;
  errors: FieldErrors<VolunteerFormState>;
  control: Control<VolunteerFormState>;
  isTablet: boolean;
  updateForm: UpdateVolunteerForm;
}

const ConductCheckStep = ({ form, errors, control, isTablet, updateForm }: Props) => {
  //   @ts-ignore
  const { t } = useTranslation();
  const [selectedFileName, setSelectedFileName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const selectYesNoMessage = t('pbl.lawyerRegistration.conductCheck.errors.selectYesNo', {
    defaultValue: 'Please select Yes or No.',
  });

  const selectConductCheckFile = async (file: File) => {
    setSelectedFileName(file.name);
    updateForm('conduct_supporting_document_file_id', undefined);

    try {
      setIsUploading(true);
      const response = await uploadPblFile(createPblUploadFormData(file, 'lawyer_conduct_supporting_document'));
      const fileId = getUploadedPblFileId(response.data.data);

      if (response.data.success === false || typeof fileId !== 'number') {
        throw new Error(
          response.data.message ||
            t('pbl.lawyerRegistration.conductCheck.errors.uploadFailed', {
              defaultValue: 'Failed to upload conduct supporting document.',
            })
        );
      }

      updateForm('conduct_supporting_document_file_id', fileId);
      updateForm('conduct_supporting_document_file', {
        file_id: fileId,
        name: file.name,
        url: createLocalDocumentUrl(file),
      });
    } catch (error: any) {
      setSelectedFileName('');
      updateForm('conduct_supporting_document_file', null);
      errorHandler(
        error.response?.data?.message ||
          error.message ||
          t('pbl.lawyerRegistration.conductCheck.errors.uploadFailed', {
            defaultValue: 'Failed to upload conduct supporting document.',
          })
      );
    } finally {
      setIsUploading(false);
    }
  };

  const clearConductCheckFile = () => {
    setSelectedFileName('');
    updateForm('conduct_supporting_document_file_id', undefined);
    updateForm('conduct_supporting_document_file', null);
  };

  return (
    <FormPanel isTablet={isTablet}>
      <SectionTitle>{t('pbl.lawyerRegistration.conductCheck.section', { defaultValue: 'Section 9' })}</SectionTitle>
      <FormSectionTitle>
        {t('pbl.lawyerRegistration.conductCheck.title', { defaultValue: 'Conduct Check' })}
      </FormSectionTitle>

      <InputWrapper marginBottom={24}>
        <Text>
          {t('pbl.lawyerRegistration.conductCheck.intro', {
            defaultValue:
              '20. I declare and confirm the following for the purpose of my application to join the Scheme:',
          })}
        </Text>
      </InputWrapper>

      <InputWrapper marginBottom={24}>
        <Controller
          control={control}
          name="bankrupt_or_insolvent"
          rules={{ validate: (value) => value !== undefined || selectYesNoMessage }}
          render={({ field, fieldState }) => (
            <FormControl error={!!fieldState.error}>
              {t('pbl.lawyerRegistration.conductCheck.questions.bankruptOrInsolvent', {
                defaultValue:
                  '(1) Have you ever been made bankrupt or insolvent, or are any such proceedings pending against you in any jurisdiction?',
              })}
              <RadioGroup
                value={field.value === undefined ? '' : field.value ? 'yes' : 'no'}
                onChange={(event) => field.onChange(event.target.value === 'yes')}
              >
                <FormControlLabel
                  value="yes"
                  control={<Radio color="primary" />}
                  label={t('pbl.common.yes', { defaultValue: 'Yes' })}
                />
                <FormControlLabel
                  value="no"
                  control={<Radio color="primary" />}
                  label={t('pbl.common.no', { defaultValue: 'No' })}
                />
              </RadioGroup>
              {fieldState.error?.message && <ValidationText>{fieldState.error.message}</ValidationText>}
            </FormControl>
          )}
        />
      </InputWrapper>

      <InputWrapper marginBottom={24}>
        <Controller
          control={control}
          name="arrangement_with_creditors"
          rules={{ validate: (value) => value !== undefined || selectYesNoMessage }}
          render={({ field, fieldState }) => (
            <FormControl error={!!fieldState.error}>
              {t('pbl.lawyerRegistration.conductCheck.questions.arrangementWithCreditors', {
                defaultValue: '(2) Have you ever entered into an arrangement or composition with creditors?',
              })}
              <RadioGroup
                value={field.value === undefined ? '' : field.value ? 'yes' : 'no'}
                onChange={(event) => field.onChange(event.target.value === 'yes')}
              >
                <FormControlLabel
                  value="yes"
                  control={<Radio color="primary" />}
                  label={t('pbl.common.yes', { defaultValue: 'Yes' })}
                />
                <FormControlLabel
                  value="no"
                  control={<Radio color="primary" />}
                  label={t('pbl.common.no', { defaultValue: 'No' })}
                />
              </RadioGroup>
              {fieldState.error?.message && <ValidationText>{fieldState.error.message}</ValidationText>}
            </FormControl>
          )}
        />
      </InputWrapper>

      <InputWrapper marginBottom={24}>
        <Controller
          control={control}
          name="criminal_conviction"
          rules={{ validate: (value) => value !== undefined || selectYesNoMessage }}
          render={({ field, fieldState }) => (
            <FormControl error={!!fieldState.error}>
              {t('pbl.lawyerRegistration.conductCheck.questions.criminalConviction', {
                defaultValue:
                  '(3) Have you been convicted of any offence in any court of Hong Kong or elsewhere (other than a motoring offence not resulting in disqualification)?',
              })}
              <RadioGroup
                value={field.value === undefined ? '' : field.value ? 'yes' : 'no'}
                onChange={(event) => field.onChange(event.target.value === 'yes')}
              >
                <FormControlLabel
                  value="yes"
                  control={<Radio color="primary" />}
                  label={t('pbl.common.yes', { defaultValue: 'Yes' })}
                />
                <FormControlLabel
                  value="no"
                  control={<Radio color="primary" />}
                  label={t('pbl.common.no', { defaultValue: 'No' })}
                />
              </RadioGroup>
              {fieldState.error?.message && <ValidationText>{fieldState.error.message}</ValidationText>}
            </FormControl>
          )}
        />
      </InputWrapper>

      <InputWrapper marginBottom={24}>
        <Controller
          control={control}
          name="professional_misconduct_or_pending_disciplinary"
          rules={{ validate: (value) => value !== undefined || selectYesNoMessage }}
          render={({ field, fieldState }) => (
            <FormControl error={!!fieldState.error}>
              {t('pbl.lawyerRegistration.conductCheck.questions.professionalMisconduct', {
                defaultValue:
                  '(4) Have you at any time been found guilty of professional misconduct by a disciplinary tribunal, or are any proceedings before a disciplinary tribunal still pending in any jurisdiction?',
              })}
              <RadioGroup
                value={field.value === undefined ? '' : field.value ? 'yes' : 'no'}
                onChange={(event) => field.onChange(event.target.value === 'yes')}
              >
                <FormControlLabel
                  value="yes"
                  control={<Radio color="primary" />}
                  label={t('pbl.common.yes', { defaultValue: 'Yes' })}
                />
                <FormControlLabel
                  value="no"
                  control={<Radio color="primary" />}
                  label={t('pbl.common.no', { defaultValue: 'No' })}
                />
              </RadioGroup>
              {fieldState.error?.message && <ValidationText>{fieldState.error.message}</ValidationText>}
            </FormControl>
          )}
        />
      </InputWrapper>

      <InputWrapper marginBottom={24}>
        <Controller
          control={control}
          name="suspended_from_professional_practice"
          rules={{ validate: (value) => value !== undefined || selectYesNoMessage }}
          render={({ field, fieldState }) => (
            <FormControl error={!!fieldState.error}>
              {t('pbl.lawyerRegistration.conductCheck.questions.suspendedFromPractice', {
                defaultValue:
                  '(5) Have you at any time been suspended from professional practice by your professional body?',
              })}
              <RadioGroup
                value={field.value === undefined ? '' : field.value ? 'yes' : 'no'}
                onChange={(event) => field.onChange(event.target.value === 'yes')}
              >
                <FormControlLabel
                  value="yes"
                  control={<Radio color="primary" />}
                  label={t('pbl.common.yes', { defaultValue: 'Yes' })}
                />
                <FormControlLabel
                  value="no"
                  control={<Radio color="primary" />}
                  label={t('pbl.common.no', { defaultValue: 'No' })}
                />
              </RadioGroup>
              {fieldState.error?.message && <ValidationText>{fieldState.error.message}</ValidationText>}
            </FormControl>
          )}
        />
      </InputWrapper>

      <InputWrapper marginBottom={24}>
        <Text>
          {t('pbl.lawyerRegistration.conductCheck.supportingDocumentNote', {
            defaultValue:
              '*If you have answered "Yes" to any question above, please provide full particulars, supporting documents, and attach them to this application.',
          })}
        </Text>
      </InputWrapper>

      <InputWrapper style={{ height: 'auto' }}>
        <UploadFileField
          name="conduct_supporting_document_file_id"
          clearAriaLabel={t('pbl.common.upload.clearConductSupportingDocument', {
            defaultValue: 'Clear conduct supporting document',
          })}
          fileName={selectedFileName || form.conduct_supporting_document_file?.name}
          isUploading={isUploading}
          onClear={clearConductCheckFile}
          onSelectFile={selectConductCheckFile}
          placeholder={t('pbl.lawyerRegistration.conductCheck.uploadPlaceholder', {
            defaultValue: 'Upload Supporting Documents for Conduct Check',
          })}
        />
        <HelperText>
          {t('pbl.common.upload.pdfMaxSize10mb', { defaultValue: 'Upload as PDF. Max size 10MB' })}
        </HelperText>
      </InputWrapper>

      {(errors.bankrupt_or_insolvent?.message ||
        errors.arrangement_with_creditors?.message ||
        errors.criminal_conviction?.message ||
        errors.professional_misconduct_or_pending_disciplinary?.message ||
        errors.suspended_from_professional_practice?.message ||
        errors.conduct_supporting_document_file_id?.message) && (
        <ValidationText>
          {errors.bankrupt_or_insolvent?.message ||
            errors.arrangement_with_creditors?.message ||
            errors.criminal_conviction?.message ||
            errors.professional_misconduct_or_pending_disciplinary?.message ||
            errors.suspended_from_professional_practice?.message ||
            errors.conduct_supporting_document_file_id?.message}
        </ValidationText>
      )}
    </FormPanel>
  );
};

export default ConductCheckStep;
